import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, ListFilter } from 'lucide-react';

interface DateCalendarProps {
  selected: string;
  onSelect: (iso: string) => void;
  /** Quanti giorni in avanti sono prenotabili (oltre = disabilitati). */
  maxAdvanceDays?: number;
  /** Giorni minimi di anticipo prima del primo slot prenotabile (default 0 = oggi). */
  minAdvanceDays?: number;
}

const DOW_SHORT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS_IT = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

function startOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseISO(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}
/** Lunedì della settimana che contiene `d` (settimana lun-dom). */
function weekStart(d: Date): Date {
  const dow = (d.getDay() + 6) % 7; // 0 = lunedì
  return startOfDay(addDays(d, -dow));
}
function startOfMonth(d: Date): Date {
  return startOfDay(new Date(d.getFullYear(), d.getMonth(), 1));
}
function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

type View = 'week' | 'month';

export default function DateCalendar({
  selected,
  onSelect,
  maxAdvanceDays = 60,
  minAdvanceDays = 0,
}: DateCalendarProps) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const minDate = useMemo(() => addDays(today, Math.max(0, minAdvanceDays)), [today, minAdvanceDays]);
  const maxDate = useMemo(
    () => addDays(today, Math.max(minAdvanceDays, maxAdvanceDays)),
    [today, minAdvanceDays, maxAdvanceDays],
  );

  const selectedDate = useMemo(() => parseISO(selected), [selected]);
  const initialAnchor = selectedDate && selectedDate >= minDate && selectedDate <= maxDate ? selectedDate : minDate;

  const [view, setView] = useState<View>('week');
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => weekStart(initialAnchor));
  const [monthAnchor, setMonthAnchor] = useState<Date>(() => startOfMonth(initialAnchor));

  const isDisabled = (d: Date): boolean => d < minDate || d > maxDate;

  const select = (d: Date): void => {
    if (isDisabled(d)) return;
    onSelect(toISO(d));
  };

  // ---- Vista settimana --------------------------------------------------
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekAnchor, i)),
    [weekAnchor],
  );
  const canPrevWeek = addDays(weekAnchor, -1) >= minDate;
  const canNextWeek = addDays(weekAnchor, 7) <= maxDate;
  const weekLabel = useMemo(() => {
    const a = weekAnchor;
    const b = addDays(weekAnchor, 6);
    if (a.getMonth() === b.getMonth()) return `${MONTHS_IT[a.getMonth()] ?? ''} ${a.getFullYear()}`;
    const left = (MONTHS_IT[a.getMonth()] ?? '').slice(0, 3);
    const right = (MONTHS_IT[b.getMonth()] ?? '').slice(0, 3);
    const yr = a.getFullYear() === b.getFullYear() ? `${a.getFullYear()}` : `${a.getFullYear()}–${b.getFullYear()}`;
    return `${left}–${right} ${yr}`;
  }, [weekAnchor]);

  // ---- Vista mese -------------------------------------------------------
  const monthCells = useMemo(() => {
    const first = startOfMonth(monthAnchor);
    const lead = (first.getDay() + 6) % 7; // celle vuote prima del giorno 1
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const cells: Array<Date | null> = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(startOfDay(new Date(first.getFullYear(), first.getMonth(), d)));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthAnchor]);
  const canPrevMonth = startOfMonth(monthAnchor) > startOfMonth(minDate);
  const canNextMonth = startOfDay(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1)) <= maxDate;

  const goToMonthView = (): void => {
    setMonthAnchor(startOfMonth(weekDays.find((d) => !isDisabled(d)) ?? weekAnchor));
    setView('month');
  };
  const goToWeekView = (): void => {
    const target = selectedDate && sameMonth(selectedDate, monthAnchor) ? selectedDate : startOfMonth(monthAnchor);
    setWeekAnchor(weekStart(target < minDate ? minDate : target));
    setView('week');
  };

  return (
    <div className="aw-cal" aria-label="Seleziona giorno">
      <div className="aw-cal-head">
        <button
          type="button"
          className="aw-cal-nav"
          aria-label={view === 'week' ? 'Settimana precedente' : 'Mese precedente'}
          disabled={view === 'week' ? !canPrevWeek : !canPrevMonth}
          onClick={() =>
            view === 'week'
              ? setWeekAnchor((w) => weekStart(addDays(w, -7)))
              : setMonthAnchor((m) => startOfMonth(new Date(m.getFullYear(), m.getMonth() - 1, 1)))
          }
        >
          <ChevronLeft size={18} />
        </button>

        <span className="aw-cal-label">
          {view === 'week' ? weekLabel : `${MONTHS_IT[monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`}
        </span>

        <button
          type="button"
          className="aw-cal-nav"
          aria-label={view === 'week' ? 'Settimana successiva' : 'Mese successivo'}
          disabled={view === 'week' ? !canNextWeek : !canNextMonth}
          onClick={() =>
            view === 'week'
              ? setWeekAnchor((w) => weekStart(addDays(w, 7)))
              : setMonthAnchor((m) => startOfMonth(new Date(m.getFullYear(), m.getMonth() + 1, 1)))
          }
        >
          <ChevronRight size={18} />
        </button>

        <button
          type="button"
          className="aw-cal-toggle"
          onClick={view === 'week' ? goToMonthView : goToWeekView}
        >
          {view === 'week' ? <CalendarDays size={15} /> : <ListFilter size={15} />}
          <span>{view === 'week' ? 'Altri mesi' : 'Giorni'}</span>
        </button>
      </div>

      {view === 'week' ? (
        <div className="aw-cal-week" role="listbox" aria-label="Giorni della settimana">
          {weekDays.map((d) => {
            const iso = toISO(d);
            const disabled = isDisabled(d);
            const isSel = iso === selected;
            const isToday = d.getTime() === today.getTime();
            return (
              <button
                key={iso}
                type="button"
                role="option"
                aria-selected={isSel}
                disabled={disabled}
                className={`aw-cal-pill ${isSel ? 'aw-selected' : ''} ${isToday ? 'aw-today' : ''}`}
                onClick={() => select(d)}
              >
                <span className="aw-cal-dow">{DOW_SHORT[(d.getDay() + 6) % 7] ?? ''}</span>
                <span className="aw-cal-num">{d.getDate()}</span>
                <span className="aw-cal-mon">{(MONTHS_IT[d.getMonth()] ?? '').slice(0, 3).toLowerCase()}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="aw-cal-month">
          <div className="aw-cal-dow-row" aria-hidden="true">
            {DOW_SHORT.map((d) => (
              <span key={d} className="aw-cal-dow">{d.charAt(0)}</span>
            ))}
          </div>
          <div className="aw-cal-grid" role="listbox" aria-label="Giorni del mese">
            {monthCells.map((d, i) => {
              if (!d) return <span key={`e${i}`} className="aw-cal-cell aw-cal-empty" aria-hidden="true" />;
              const iso = toISO(d);
              const disabled = isDisabled(d);
              const isSel = iso === selected;
              const isToday = d.getTime() === today.getTime();
              return (
                <button
                  key={iso}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  disabled={disabled}
                  className={`aw-cal-cell ${isSel ? 'aw-selected' : ''} ${isToday ? 'aw-today' : ''}`}
                  onClick={() => select(d)}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
